/* eslint-disable complexity */
import React from "react";
import { Control, useFormContext, Controller, useFieldArray, useWatch } from "react-hook-form";
import { Stack, RadioGroup, Radio, FormControlLabel, FormControl, FormLabel, Box, Button, IconButton, TextField, MenuItem, Autocomplete } from "@mui/material";

import {
  FormInputSelect,
  FormInputTel,
  FormInputText,
  FormSwitch,
} from "@imminently/react/ui/forms";

import {
  FormAllergies,
  FormComplaintCodeSelect,
  FormLocationSelect,
  FormPatientTasks,
  FormSymptomsSelect,
  FormTriageSelect,
} from "../forms";
import PatientNameField from "./PatientNameField";
import DateOfBirthField from "./DateOfBirthField";
import DelegatePhoneField from "./DelegatePhoneField";
import PatientStatusWidget from "./PatientStatusWidget";
import ExistingPatientLinkChip from "./ExistingPatientLinkChip";
import { useTypes } from "@/providers/TypesProvider";
import { useFilteredOptions } from "@/providers/useFilteredOptions";
import { PAIN_TYPES, FRAILTY_TYPES } from "@/resources/constants";
import { Text } from "@imminently/react/ui/core/text";
import { css } from "@/theme";
import { Icon } from "@/components/Icon";

const withAutocompleteOffSlotProps = (slotProps?: any) => ({
  ...slotProps,
  htmlInput: {
    ...slotProps?.htmlInput,
    autoComplete: "off",
  },
});

// Custom FormInputText that allows suppressing errors (bypassing Controller's internal error state)
const CustomFormInputText: React.FC<{
  name: string;
  control: Control<any>;
  label?: string;
  disabled?: boolean;
  required?: boolean;
  sx?: any;
  error?: boolean; // Explicit override
  helperText?: string; // Explicit override
  slotProps?: any;
  [key: string]: any;
}> = ({ name, control, label, disabled, required, sx, error: errorProp, helperText: helperTextProp, slotProps, ...props }) => {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error: fieldError } }) => {
        // If error prop is explicitly provided (true/false), use it. Otherwise use fieldError.
        // This allows us to pass false to suppress errors even if fieldError exists.
        const hasError = errorProp !== undefined ? errorProp : !!fieldError;
        const helperText = helperTextProp !== undefined ? helperTextProp : (fieldError ? fieldError.message : " ");

        return (
          <TextField
            {...field}
            label={label}
            disabled={disabled}
            required={required}
            error={hasError}
            helperText={helperText}
            sx={sx}
            variant="outlined"
            fullWidth
            {...props}
            autoComplete="off"
            slotProps={withAutocompleteOffSlotProps(slotProps)}
          />
        );
      }}
    />
  );
};

// Custom FormInputSelect that allows suppressing errors
export const CustomFormInputSelect: React.FC<{
  name: string;
  control: Control<any>;
  label?: string;
  options: { label: string; value: any; disabled?: boolean }[];
  disabled?: boolean;
  sx?: any;
  error?: boolean; // Explicit override
  helperText?: string; // Explicit override
  slotProps?: any;
  [key: string]: any;
}> = ({ name, control, label, options, disabled, sx, error: errorProp, helperText: helperTextProp, slotProps, ...props }) => {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error: fieldError } }) => {
        const hasError = errorProp !== undefined ? errorProp : !!fieldError;
        const helperText = helperTextProp !== undefined ? helperTextProp : (fieldError ? fieldError.message : " ");

        return (
          <TextField
            {...field}
            select
            label={label}
            disabled={disabled}
            error={hasError}
            helperText={helperText}
            sx={sx}
            variant="outlined"
            fullWidth
            {...props}
            autoComplete="off"
            slotProps={withAutocompleteOffSlotProps(slotProps)}
          >
            {options.map((option) => (
              <MenuItem key={option.value} value={option.value} disabled={option.disabled}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        );
      }}
    />
  );
};

// Custom FormAutocomplete with type-ahead support
// Supports error suppression via explicit error/helperText props (same as CustomFormInputSelect)
export const CustomFormAutocomplete: React.FC<{
  name: string;
  control: Control<any>;
  label?: string;
  options: { label: string; value: any; disabled?: boolean }[];
  disabled?: boolean;
  required?: boolean;
  sx?: any;
  error?: boolean; // Explicit override - pass false to suppress errors
  helperText?: string; // Explicit override
  freeSolo?: boolean; // Allow custom values not in options
  [key: string]: any;
}> = ({ name, control, label, options, disabled, required, sx, error: errorProp, helperText: helperTextProp, freeSolo = false, ...props }) => {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error: fieldError } }) => {
        // If error prop is explicitly provided (true/false), use it. Otherwise use fieldError.
        // This allows passing false to suppress errors even if fieldError exists.
        const hasError = errorProp !== undefined ? errorProp : !!fieldError;
        const helperText = helperTextProp !== undefined ? helperTextProp : (fieldError ? fieldError.message : " ");

        // Find the selected option object from the value
        let selectedOption = options.find(opt => opt.value === field.value) || null;

        // If freeSolo and value doesn't match any option, create a temporary option for display
        if (freeSolo && field.value && !selectedOption) {
          selectedOption = { value: field.value, label: field.value };
        }

        return (
          <Autocomplete
            freeSolo={freeSolo}
            value={selectedOption}
            onChange={(_, newValue) => {
              if (freeSolo && typeof newValue === "string") {
                // User typed a custom value
                field.onChange(newValue);
              } else if (newValue && typeof newValue === "object") {
                // User selected an option
                field.onChange(newValue.value);
              } else {
                // Cleared
                field.onChange("");
              }
            }}
            onBlur={field.onBlur}
            options={options}
            getOptionLabel={(option) => {
              if (typeof option === "string") return option;
              return option.label;
            }}
            isOptionEqualToValue={(option, value) => {
              if (typeof option === "string" || typeof value === "string") {
                return option === value;
              }
              return option.value === value.value;
            }}
            disabled={disabled}
            fullWidth
            autoHighlight
            openOnFocus
            sx={{
              "& .MuiInputBase-root": {
                height: "38.99px",
                padding: "0 9px",
              },
              "& .MuiInputBase-input": {
                padding: "8.5px 4px !important",
              },
              ...sx,
            }}
            renderInput={({ inputProps, ...params }) => (
              <TextField
                {...params}
                label={label}
                required={required}
                error={hasError}
                helperText={helperText}
                variant="outlined"
                autoComplete="off"
                slotProps={withAutocompleteOffSlotProps({ htmlInput: inputProps })}
              />
            )}
            {...props}
          />
        );
      }}
    />
  );
};

// Component for a single previous name entry
const PreviousNameEntry: React.FC<{
  name: string;
  index: number;
  control: Control<any>;
  onRemove: () => void;
}> = ({ name, index, control, onRemove }) => {
  const { formState: { errors, isSubmitted, touchedFields }, trigger } = useFormContext();

  const firstNameError = (errors as any)?.[name]?.[index]?.firstName;
  const lastNameError = (errors as any)?.[name]?.[index]?.lastName;
  // Check if this entry has an all-or-none error (display once per entry)
  const hasAllOrNoneError = (firstNameError && firstNameError.type === "prev-name-all-or-none") ||
                            (lastNameError && lastNameError.type === "prev-name-all-or-none");
  const entryError = firstNameError || lastNameError;
  const entryErrorMessage = hasAllOrNoneError ? entryError?.message : undefined;

  // Watch both fields for this entry and trigger validation when either changes
  const firstNameValue = useWatch({ control, name: `${name}.${index}.firstName` });
  const lastNameValue = useWatch({ control, name: `${name}.${index}.lastName` });
  const firstNameTouched = (touchedFields as any)?.[name]?.[index]?.firstName;
  const lastNameTouched = (touchedFields as any)?.[name]?.[index]?.lastName;
  const entryTouched = firstNameTouched || lastNameTouched;

  React.useEffect(() => {
    // Trigger validation if form is submitted or at least one field in this entry has been touched
    // This ensures errors clear when the last missing field is filled
    if (isSubmitted || entryTouched) {
      trigger([`${name}.${index}.firstName`, `${name}.${index}.lastName`]);
    }
  }, [firstNameValue, lastNameValue, isSubmitted, entryTouched, trigger, name, index]);

  return (
    <Stack gap={0.5}>
      {hasAllOrNoneError && entryErrorMessage && (
        <Text variant="body2" color="error">
          {entryErrorMessage}
        </Text>
      )}
      <Stack direction="row" gap={1} alignItems="center">
        <CustomFormInputText
          name={`${name}.${index}.lastName`}
          label="Last name"
          control={control}
          sx={{ flex: 1 }}
          error={hasAllOrNoneError ? false : undefined}
          helperText={hasAllOrNoneError ? " " : undefined}
        />
        <CustomFormInputText
          name={`${name}.${index}.firstName`}
          label="First name"
          control={control}
          sx={{ flex: 1 }}
          error={hasAllOrNoneError ? false : undefined}
          helperText={hasAllOrNoneError ? " " : undefined}
        />
        <IconButton
          onClick={onRemove}
          size="small"
          color="error"
          sx={{ mb: 2 }}
        >
          <Icon icon="delete" />
        </IconButton>
      </Stack>
    </Stack>
  );
};

// FormInputArray component for previous names
const FormInputArray: React.FC<{
  name: string;
  label?: string;
  control: Control<any>;
}> = ({ name, label, control }) => {
  const { fields, append, remove } = useFieldArray({
    control,
    name,
  });

  return (
    <Stack gap={2}>
      {label && <Text variant="subtitle2" sx={{ fontWeight: 600 }}>{label}</Text>}
      {fields.map((field, index) => (
        <PreviousNameEntry
          key={field.id}
          name={name}
          index={index}
          control={control}
          onRemove={() => remove(index)}
        />
      ))}
      <Button
        variant="outlined"
        onClick={() => append({ firstName: "", lastName: "" })}
        startIcon={<Icon icon="add" />}
        sx={{ alignSelf: "flex-start" }}
      >
        Add Previous Name
      </Button>
    </Stack>
  );
};

// Field configuration interface
export interface FieldConfig {
  id: string;
  label?: string;
  required?: boolean;
  placeholder?: string;
  helpText?: string;
  disabled?: boolean;
  sx?: any;
}

// Field renderer function type
export type FieldRenderer = (config: FieldConfig, control: Control<any>) => React.ReactElement;

// Field registry mapping field IDs to their renderers
export const fieldRegistry: Record<string, FieldRenderer> = {
  // Patient identity fields
  lastName: (config, control) => (
    <PatientNameField
      name="lastName"
      label={config.label || "Last name"}
      required={config.required}
      disabled={config.disabled}
      sx={{ flex: 1, ...config.sx }}
    />
  ),

  firstName: (config, control) => (
    <PatientNameField
      name="firstName"
      label={config.label || "First name"}
      required={config.required}
      disabled={config.disabled}
      sx={{ flex: 1, ...config.sx }}
    />
  ),

  existingPatientLinkChip: () => (
    <ExistingPatientLinkChip />
  ),

  patientStatusWidget: (_config, control) => (
    <PatientStatusWidget control={control} />
  ),

  sexAtBirth: (config, control) => {
    const { sexOptions } = useTypes();
    const filteredOptions = useFilteredOptions("sex", sexOptions);
    return (
      <CustomFormAutocomplete
        sx={{ width: "100%", ...config.sx }}
        name="sex"
        label={config.label || "Sex at birth"}
        options={filteredOptions}
        control={control}
        disabled={config.disabled}
        required={config.required}
      />
    );
  },

  gender: (config, control) => {
    const { genderOptions } = useTypes();
    const filteredOptions = useFilteredOptions("gender", genderOptions);
    return (
      <CustomFormAutocomplete
        sx={{ width: "100%", ...config.sx }}
        name="gender"
        label={config.label || "Gender"}
        options={filteredOptions}
        control={control}
        disabled={config.disabled}
      />
    );
  },

  dateOfBirth: (config, control) => (
    <DateOfBirthField
      name="dob"
      label={config.label}
      format={config.placeholder || "DD/MM/YYYY"}
      control={control}
      disabled={config.disabled}
      required={config.required}
      sx={config.sx}
    />
  ),

  medicareNumber: (config, control) => (
    <FormInputText
      name="medicareNo"
      label={config.label || "Medicare Number"}
      helperText={config.helpText || "Optional"}
      sx={{ width: "100%", ...config.sx }}
      control={control}
      disabled={config.disabled}
      autoComplete="off"
      slotProps={withAutocompleteOffSlotProps()}
    />
  ),

  medicarePosition: (config, control) => (
    <FormInputText
      name="medicareNoPos"
      label={config.label || "Position"}
      sx={{ width: "100%", ...config.sx }}
      control={control}
      disabled={config.disabled}
      autoComplete="off"
      slotProps={withAutocompleteOffSlotProps()}
    />
  ),

  postcode: (config, control) => (
    <FormInputText
      name="postcode"
      label={config.label || "Postcode"}
      sx={{ flex: 1, ...config.sx }}
      control={control}
      disabled={config.disabled}
      autoComplete="off"
      slotProps={withAutocompleteOffSlotProps()}
    />
  ),

  umrn: (config, control) => (
    <FormInputText
      name="umrn"
      label={config.label || "UMRN"}
      helperText={config.helpText || "Hospital Unit Medical Record Number"}
      sx={{ flex: 1, ...config.sx }}
      control={control}
      disabled={config.disabled}
      autoComplete="off"
      slotProps={withAutocompleteOffSlotProps()}
    />
  ),

  phone: (config, control) => (
    <FormInputTel
      name="phone"
      label={config.label || "Phone"}
      sx={{ flex: 1, ...config.sx }}
      control={control}
      disabled={config.disabled}
      required={config.required}
      autoComplete="off"
      slotProps={withAutocompleteOffSlotProps()}
    />
  ),

  email: (config, control) => (
    <FormInputText
      name="email"
      label={config.label || "Email"}
      sx={{ flex: 1.2, ...config.sx }}
      control={control}
      disabled={config.disabled}
      required={config.required}
      autoComplete="off"
      slotProps={withAutocompleteOffSlotProps()}
    />
  ),

  registerAs: (config, control) => {
    const { setValue, getValues } = useFormContext();

    const isEditMode = useWatch({ control, name: "_isEditMode" });
    const encounterId = useWatch({ control, name: "_encounterId" });
    const existingPatientId = useWatch({ control, name: "_existingPatientId" });
    // const submissionType = watch("submissionType");

    // Store the initial submission type on mount
    const [initialSubmissionType] = React.useState(() => getValues("submissionType"));

    const isEditing = isEditMode || !!encounterId || !!existingPatientId;

    // In edit mode, determine what type of patient this ORIGINALLY was (use initial type)
    const wasKnown = isEditing && initialSubmissionType === ""; // Originally Standard patient
    // const wasUnknown = isEditing && initialSubmissionType === "unknown";
    const wasAnonymous = isEditing && initialSubmissionType === "anonymous";

    // Ensure flags are set on mount
    React.useEffect(() => {
      const currentSubmissionType = getValues("submissionType");
      if (currentSubmissionType === "anonymous") {
        setValue("anonymous", true);
        setValue("unknown", false);
      } else if (currentSubmissionType === "unknown") {
        setValue("anonymous", false);
        setValue("unknown", true);
      } else {
        // Empty string or any other value = standard
        setValue("anonymous", false);
        setValue("unknown", false);
      }
    }, [setValue, getValues]);

    const radioStyles = css`
      gap: 0.5rem;
      & .MuiFormControlLabel-root {
        align-items: center;
        gap: 0;
      }
      & .MuiRadio-root {
        width: 100%;
      }
      & .MuiTypography-root {
        font-size: 0.875rem;
        font-weight: normal;
      }
    `;

    return (
      <Controller
        name="submissionType"
        control={control}
        render={({ field }) => (
          <FormControl sx={{ flex: 1.2 }}>
            <Stack direction="row" alignItems="center" gap={2}>
              {config.label ? <FormLabel id="patient-as" sx={{ margin: 0, minWidth: "fit-content" }}>
                {config.label }
              </FormLabel> : null}
              <RadioGroup
                aria-labelledby="patient-as"
                {...field}
                css={radioStyles}
                row
                onChange={(e) => {
                  const value = e.target.value;
                  field.onChange(value);
                  // Set anonymous/unknown flags based on selection
                  if (value === "anonymous") {
                    setValue("anonymous", true);
                    setValue("unknown", false);
                  } else if (value === "unknown") {
                    setValue("anonymous", false);
                    setValue("unknown", true);
                  } else {
                    setValue("anonymous", false);
                    setValue("unknown", false);
                  }
                }}
              >
                <FormControlLabel
                  value=""
                  control={<Radio size="small" />}
                  label="Standard"
                  labelPlacement="end"
                  disabled={false} // Allow de-anonymization (Anonymous → Standard)
                />
                <FormControlLabel
                  value="unknown"
                  control={<Radio />}
                  label="Unknown"
                  labelPlacement="end"
                  disabled={isEditing && (wasKnown || wasAnonymous)} // Can't change Standard or Anonymous to Unknown
                />
                <FormControlLabel
                  value="anonymous"
                  control={<Radio />}
                  label="Anonymous"
                  labelPlacement="end"
                  disabled={isEditing && wasAnonymous} // Can't change Anonymous to Anonymous, but Standard/Unknown can become Anonymous
                />
              </RadioGroup>
            </Stack>
          </FormControl>
        )}
      />
    );
  },

  // Assessment fields
  expects: (config, control) => {
    const expectsValue = useWatch({ control, name: "expects" });
    const triageDirectionValue = useWatch({ control, name: "triageDirection" });
    const { triageDirectionOptions } = useTypes();

    if (!expectsValue) {
      return <React.Fragment />;
    }

    // Find the label for triageDirection if it exists
    const triageDirectionLabel = triageDirectionValue
      ? triageDirectionOptions.find(opt => opt.value === triageDirectionValue)?.label || triageDirectionValue
      : null;

    return (
      <Box sx={{ mb: 1 }}>
        <Text variant="caption" sx={{ fontSize: "0.75rem", fontWeight: 500, mb: 0.5, display: "block" }}>
          {config.label || "Expected complaint"}
        </Text>
        <Box
          sx={{
            p: 1.5,
            backgroundColor: "grey.50",
            border: "1px solid",
            borderColor: "grey.300",
            borderRadius: "0.5rem",
          }}
        >
          <Text variant="body2" sx={{ color: "text.secondary" }}>
            {expectsValue}
          </Text>
        </Box>
        {triageDirectionLabel && (
          <>
            <Text variant="caption" sx={{ fontSize: "0.75rem", fontWeight: 500, mb: 0.5, mt: 1, display: "block" }}>
              Triage directions
            </Text>
            <Box
              sx={{
                p: 1.5,
                backgroundColor: "grey.50",
                border: "1px solid",
                borderColor: "grey.300",
                borderRadius: "0.5rem",
              }}
            >
              <Text variant="body2" sx={{ color: "text.secondary" }}>
                {triageDirectionLabel}
              </Text>
            </Box>
          </>
        )}
      </Box>
    );
  },

  presentingComplaint: (config, control) => (
    <FormInputText
      required={config.required}
      name="problem"
      label={config.label || "Presenting complaint (specify details)"}
      multiline
      minRows={3}
      control={control}
      disabled={config.disabled}
      autoComplete="off"
      slotProps={withAutocompleteOffSlotProps()}
      autoFocus
    />
  ),
  patientComplaint: (config, control) => (
    <FormInputText
      required={config.required}
      name="patientComplaint"
      label={config.label || "Patient complaint"}
      multiline
      minRows={3}
      control={control}
      disabled={config.disabled}
      autoComplete="off"
      slotProps={withAutocompleteOffSlotProps()}
      autoFocus
    />
  ),

  complaintCode: (config, control) => (
    <FormComplaintCodeSelect
      control={control}
      name="complaintCode"
      label={config.label || "Complaint Code"}
      required={config.required}
    />
  ),

  triageCategory: (config, control) => (
    <FormTriageSelect
      control={control}
      name="atsScore"
      label={config.label || "Triage category"}
      required={config.required}
    />
  ),

  nursingAssessment: (config, control) => (
    <FormInputText
      name="nursingAssessment"
      label={config.label || "Nursing assessment"}
      multiline
      minRows={3}
      control={control}
      disabled={config.disabled}
      autoComplete="off"
      slotProps={withAutocompleteOffSlotProps()}
    />
  ),

  // Visit details
  typeOfVisit: (config, control) => {
    const { typeOfVisitOptions } = useTypes();
    const filteredOptions = useFilteredOptions("typeOfVisit", typeOfVisitOptions);
    return (
      <CustomFormAutocomplete
        name="typeOfVisit"
        label={config.label || "Type of Visit"}
        options={filteredOptions}
        sx={{ flex: 1, ...config.sx }}
        control={control}
        disabled={config.disabled}
        required={config.required}
      />
    );
  },

  modeOfArrival: (config, control) => {
    const { modeOfArrivalOptions } = useTypes();
    const filteredOptions = useFilteredOptions("modeOfArrival", modeOfArrivalOptions);
    return (
      <CustomFormAutocomplete
        name="modeOfArrival"
        label={config.label || "Mode of arrival"}
        options={filteredOptions}
        sx={{ flex: 1, ...config.sx }}
        control={control}
        disabled={config.disabled}
        required={config.required}
      />
    );
  },

  location: (config, control) => (
    <FormLocationSelect
      name="location"
      label={config.label || "Location"}
      control={control}
      required={config.required}
      sx={{ width: "100%", ...config.sx }}
    />
  ),

  ambulanceCaseNumber: (config, control) => (
    <FormInputText
      name="ambulanceCaseNumber"
      label={config.label || "Ambulance case number"}
      sx={{ flex: 1, ...config.sx }}
      control={control}
      disabled={config.disabled}
      required={config.required}
      autoComplete="off"
      slotProps={withAutocompleteOffSlotProps()}
    />
  ),

  triageDirection: (config, control) => {
    const { triageDirectionOptions } = useTypes();
    const filteredOptions = useFilteredOptions("triageDirection", triageDirectionOptions);
    return (
      <CustomFormAutocomplete
        name="triageDirection"
        label={config.label || "Triage directions"}
        options={filteredOptions}
        sx={{ flex: 1, ...config.sx }}
        control={control}
        disabled={config.disabled}
      />
    );
  },

  patientOrigin: (config, control) => {
    const { patientOriginOptions } = useTypes();
    const filteredOptions = useFilteredOptions("patientOrigin", patientOriginOptions);
    return (
      <CustomFormAutocomplete
        name="patientOrigin"
        label={config.label || "Patient origin"}
        options={filteredOptions}
        sx={{ flex: 1, ...config.sx }}
        control={control}
        disabled={config.disabled}
      />
    );
  },

  // Additional information
  painScore: (config, control) => (
    <CustomFormAutocomplete
      sx={{ width: "100%", ...config.sx }}
      name="pain"
      label={config.label || "Pain score"}
      options={PAIN_TYPES.map(opt => ({ ...opt, value: String(opt.value) }))}
      control={control}
      disabled={config.disabled}
    />
  ),

  frailty: (config, control) => (
    <CustomFormAutocomplete
      sx={{ width: "100%", ...config.sx }}
      name="frailty"
      label={config.label || "Frailty"}
      options={FRAILTY_TYPES.map(opt => ({ ...opt, value: String(opt.value) }))}
      control={control}
      disabled={config.disabled}
    />
  ),

  infectiousSymptoms: (config, control) => (
    <FormSymptomsSelect
      name="infectiousSymptoms"
      label={config.label || null}
      control={control}
      sx={{ width: "100%", ...config.sx }}
    />
  ),

  allergies: (config, control) => (
    <FormAllergies
      control={control}
      name="allergies"
      disabled={config.disabled}
    />
  ),

  noAllergies: (config, control) => {
    // This component watches the allergies array to disable the toggle when allergies exist
    const NoAllergiesField = () => {
      const allergies = useWatch({ control, name: "allergies" }) || [];
      const hasAllergies = Array.isArray(allergies) && allergies.length > 0;

      return (
        <FormSwitch
          name="noAllergies"
          label={config.label || "Patient has no known allergies"}
          control={control}
          disabled={hasAllergies || config.disabled}
          slotProps={{
            label: {
              labelPlacement: "start",
              sx: { width: "100%", m: 0, justifyContent: "space-between" }
            }
          }}
        />
      );
    };

    return <NoAllergiesField />;
  },

  relatedAlcoholDrugs: (config, control) => (
    <FormSwitch
      name="relatedAlcoholDrugs"
      label={config.label || "Alcohol, Drugs"}
      control={control}
      slotProps={{
        label: {
          labelPlacement: "start",
          sx: { width: "100%", m: 0, justifyContent: "space-between" }
        }
      }}
    />
  ),

  fdvRelated: (config, control) => (
    <FormSwitch
      name="fdvRelated"
      label={config.label || "FDV"}
      control={control}
      slotProps={{
        label: {
          labelPlacement: "start",
          sx: { width: "100%", m: 0, justifyContent: "space-between" }
        }
      }}
    />
  ),

  representIn48h: (config, control) => (
    <FormSwitch
      name="representIn48h"
      label={config.label || "Represent in 48h"}
      control={control}
      slotProps={{
        label: {
          labelPlacement: "start",
          sx: { width: "100%", m: 0, justifyContent: "space-between" }
        }
      }}
    />
  ),

  injuries: (config, control) => (
    <FormSwitch
      name="injury"
      label={config.label || "Injuries"}
      control={control}
      disabled={config.disabled}
      slotProps={{
        label: {
          labelPlacement: "start",
          sx: { width: "100%", m: 0, justifyContent: "space-between" }
        }
      }}
    />
  ),

  stream: (config, control) => {
    const { streamOptions } = useTypes();
    const filteredOptions = useFilteredOptions("stream", streamOptions);
    return (
      <CustomFormAutocomplete
        name="stream"
        label={config.label || "Stream"}
        options={filteredOptions}
        sx={{ flex: 1, ...config.sx }}
        control={control}
        disabled={config.disabled}
        required={config.required}
      />
    );
  },

  cause: (config, control) => {
    const { injuryCauseOptions } = useTypes();
    const filteredOptions = useFilteredOptions("cause", injuryCauseOptions);
    return (
      <CustomFormAutocomplete
        name="cause"
        label={config.label || "Cause"}
        options={filteredOptions}
        sx={{ width: "100%", ...config.sx }}
        control={control}
        disabled={config.disabled}
        required={config.required}
      />
    );
  },

  intent: (config, control) => {
    const { injuryIntentOptions } = useTypes();
    const filteredOptions = useFilteredOptions("intent", injuryIntentOptions);
    return (
      <CustomFormAutocomplete
        name="intent"
        label={config.label || "Intent"}
        options={filteredOptions}
        sx={{ width: "100%", ...config.sx }}
        control={control}
        disabled={config.disabled}
        required={config.required}
      />
    );
  },

  pronouns: (config, control) => {
    const { pronounsOptions } = useTypes();
    const filteredOptions = useFilteredOptions("pronouns", pronounsOptions);
    return (
      <CustomFormAutocomplete
        sx={{ width: "100%", ...config.sx }}
        name="pronouns"
        label={config.label || "Pronouns"}
        options={filteredOptions}
        control={control}
        disabled={config.disabled}
      />
    );
  },

  // Tasks
  tasks: (config, control) => (
    <Box sx={{ width: "100%" }}>
      <FormPatientTasks
        control={control}
        name="tasks"
      />
    </Box>
  ),

  // Delegate fields
  hasDelegatePresent: (config, control) => (
    <FormSwitch
      name="hasDelegatePresent"
      label={config.label || "Encounter created with delegate present"}
      control={control}
      sx={{ mr: 2, ...config.sx }}
      slotProps={{
        label: {
          labelPlacement: "end"
        }
      }}
    />
  ),

  delegateLastName: (config, control) => (
    <FormInputText
      required={config.required}
      sx={{ flex: 1, ...config.sx }}
      name="delegateLastName"
      label={config.label || "Delegate last name"}
      control={control}
      disabled={config.disabled}
      autoComplete="off"
      slotProps={withAutocompleteOffSlotProps()}
    />
  ),

  delegateFirstName: (config, control) => (
    <FormInputText
      required={config.required}
      sx={{ flex: 1, ...config.sx }}
      name="delegateFirstName"
      label={config.label || "Delegate first name"}
      control={control}
      disabled={config.disabled}
      autoComplete="off"
      slotProps={withAutocompleteOffSlotProps()}
    />
  ),

  delegatePhone: (config, control) => {
    return (
      <DelegatePhoneField
        name="delegatePhone"
        label={config.label || "Delegate phone"}
        sx={{ flex: 1, ...config.sx }}
        disabled={config.disabled}
        required={config.required}
      />
    );
  },

  delegateEmail: (config, control) => {
    const { formState: { errors } } = useFormContext();
    return (
      <FormInputText
        name="delegateEmail"
        label={config.label || "Delegate email"}
        sx={{ flex: 1, ...config.sx }}
        control={control}
        disabled={config.disabled}
        required={config.required}
        error={!!errors.delegateEmail}
        helperText={(errors.delegateEmail?.message as string) || undefined}
        autoComplete="off"
        slotProps={withAutocompleteOffSlotProps()}
      />
    );
  },

  delegateRelationship: (config, control) => {
    const { delegateRelationshipOptions } = useTypes();
    const filteredOptions = useFilteredOptions("delegateRelationship", delegateRelationshipOptions);
    return (
      <CustomFormAutocomplete
        name="delegateRelationship"
        label={config.label || "Relationship to patient"}
        options={filteredOptions}
        sx={{ width: "100%", ...config.sx }}
        control={control}
        disabled={config.disabled}
      />
    );
  },

  delegateCanAct: (config, control) => (
    <FormSwitch
      name="delegateCanAct"
      label={config.label || "Can act on behalf of patient"}
      control={control}
      sx={{ mr: 2, ...config.sx }}
      slotProps={{
        label: {
          labelPlacement: "end"
        }
      }}
    />
  ),

  // Task buttons (these would typically be custom components, but using switches for now)
  medication: (config, control) => (
    <FormSwitch
      name="medication"
      label={config.label || "Medication"}
      control={control}
      slotProps={{
        label: {
          labelPlacement: "start",
          sx: { flex: 1, m: 0, justifyContent: "space-between" }
        }
      }}
    />
  ),

  pregnancyTest: (config, control) => (
    <FormSwitch
      name="pregnancyTest"
      label={config.label || "Pregnancy Test"}
      control={control}
      slotProps={{
        label: {
          labelPlacement: "start",
          sx: { flex: 1, m: 0, justifyContent: "space-between" }
        }
      }}
    />
  ),

  bloodTest: (config, control) => (
    <FormSwitch
      name="bloodTest"
      label={config.label || "Blood Test"}
      control={control}
      slotProps={{
        label: {
          labelPlacement: "start",
          sx: { flex: 1, m: 0, justifyContent: "space-between" }
        }
      }}
    />
  ),

  ecncReview: (config, control) => (
    <FormSwitch
      name="ecncReview"
      label={config.label || "ECNC review"}
      control={control}
      slotProps={{
        label: {
          labelPlacement: "start",
          sx: { flex: 1, m: 0, justifyContent: "space-between" }
        }
      }}
    />
  ),

  xray: (config, control) => (
    <FormSwitch
      name="xray"
      label={config.label || "X-Ray"}
      control={control}
      slotProps={{
        label: {
          labelPlacement: "start",
          sx: { flex: 1, m: 0, justifyContent: "space-between" }
        }
      }}
    />
  ),

  mrat: (config, control) => (
    <FormSwitch
      name="mrat"
      label={config.label || "MRAT"}
      control={control}
      slotProps={{
        label: {
          labelPlacement: "start",
          sx: { flex: 1, m: 0, justifyContent: "space-between" }
        }
      }}
    />
  ),

  bladderScan: (config, control) => (
    <FormSwitch
      name="bladderScan"
      label={config.label || "Bladder Scan"}
      control={control}
      slotProps={{
        label: {
          labelPlacement: "start",
          sx: { flex: 1, m: 0, justifyContent: "space-between" }
        }
      }}
    />
  ),

  ecg: (config, control) => (
    <FormSwitch
      name="ecg"
      label={config.label || "ECG"}
      control={control}
      slotProps={{
        label: {
          labelPlacement: "start",
          sx: { flex: 1, m: 0, justifyContent: "space-between" }
        }
      }}
    />
  ),

  urineTest: (config, control) => (
    <FormSwitch
      name="urineTest"
      label={config.label || "Urine Test"}
      control={control}
      slotProps={{
        label: {
          labelPlacement: "start",
          sx: { flex: 1, m: 0, justifyContent: "space-between" }
        }
      }}
    />
  ),

  other: (config, control) => (
    <FormSwitch
      name="other"
      label={config.label || "Other"}
      control={control}
      slotProps={{
        label: {
          labelPlacement: "start",
          sx: { flex: 1, m: 0, justifyContent: "space-between" }
        }
      }}
    />
  ),

  // GP Details fields
  gpClinicName: (config, control) => {
    const { formState: { errors } } = useFormContext();
    const error = errors.gpClinicName;
    // Suppress all-or-none errors (they're displayed at group level)
    const shouldSuppressError = error && error.type === "gp-all-or-none";
    return (
      <CustomFormInputText
        name="gpClinicName"
        label={config.label || "Clinic name"}
        control={control}
        sx={{ width: "100%", ...config.sx }}
        disabled={config.disabled}
        error={shouldSuppressError ? false : undefined}
        helperText={shouldSuppressError ? " " : undefined}
      />
    );
  },

  gpDoctorFirstName: (config, control) => {
    const { formState: { errors } } = useFormContext();
    const error = errors.gpDoctorFirstName;
    // Suppress all-or-none errors (they're displayed at group level)
    const shouldSuppressError = error && error.type === "gp-all-or-none";
    return (
      <CustomFormInputText
        name="gpDoctorFirstName"
        label={config.label || "Doctor first name"}
        control={control}
        sx={{ width: "100%", ...config.sx }}
        disabled={config.disabled}
        error={shouldSuppressError ? false : undefined}
        helperText={shouldSuppressError ? " " : undefined}
      />
    );
  },

  gpDoctorLastName: (config, control) => {
    const { formState: { errors } } = useFormContext();
    const error = errors.gpDoctorLastName;
    // Suppress all-or-none errors (they're displayed at group level)
    const shouldSuppressError = error && error.type === "gp-all-or-none";
    return (
      <CustomFormInputText
        name="gpDoctorLastName"
        label={config.label || "Doctor last name"}
        control={control}
        sx={{ width: "100%", ...config.sx }}
        disabled={config.disabled}
        error={shouldSuppressError ? false : undefined}
        helperText={shouldSuppressError ? " " : undefined}
      />
    );
  },

  // Next of Kin fields
  nextOfKinFirstName: (config, control) => {
    const { formState: { errors } } = useFormContext();
    const error = errors.nextOfKinFirstName;
    // Suppress all-or-none errors (they're displayed at group level)
    const shouldSuppressError = error && error.type === "nok-all-or-none";
    return (
      <CustomFormInputText
        name="nextOfKinFirstName"
        label={config.label || "First name"}
        control={control}
        sx={{ width: "100%", ...config.sx }}
        disabled={config.disabled}
        error={shouldSuppressError ? false : undefined}
        helperText={shouldSuppressError ? " " : undefined}
      />
    );
  },

  nextOfKinLastName: (config, control) => {
    const { formState: { errors } } = useFormContext();
    const error = errors.nextOfKinLastName;
    // Suppress all-or-none errors (they're displayed at group level)
    const shouldSuppressError = error && error.type === "nok-all-or-none";
    return (
      <CustomFormInputText
        name="nextOfKinLastName"
        label={config.label || "Last name"}
        control={control}
        sx={{ width: "100%", ...config.sx }}
        disabled={config.disabled}
        error={shouldSuppressError ? false : undefined}
        helperText={shouldSuppressError ? " " : undefined}
      />
    );
  },

  nextOfKinRelationship: (config, control) => {
    const { delegateRelationshipOptions } = useTypes();
    const { formState: { errors } } = useFormContext();
    const filteredOptions = useFilteredOptions("nextOfKinRelationship", delegateRelationshipOptions);
    const error = errors.nextOfKinRelationship;
    // Suppress all-or-none errors (they're displayed at group level)
    const shouldSuppressError = error && error.type === "nok-all-or-none";
    return (
      <CustomFormAutocomplete
        name="nextOfKinRelationship"
        label={config.label || "Relationship"}
        options={filteredOptions}
        sx={{ flex: 1, ...config.sx }}
        control={control}
        disabled={config.disabled}
        error={shouldSuppressError ? false : undefined}
        helperText={shouldSuppressError ? " " : undefined}
      />
    );
  },

  // Previous Names field
  previousNames: (config, control) => (
    <FormInputArray
      name="previousNames"
      label={config.label}
      control={control}
    />
  ),
};

// Helper function to get field renderer
export const getFieldRenderer = (fieldId: string): FieldRenderer | undefined => {
  return fieldRegistry[fieldId];
};

// Helper function to check if field exists
export const hasField = (fieldId: string): boolean => {
  return fieldId in fieldRegistry;
};
